import { CONFIG } from './config.js';
import { logError, logWarning } from './error-handler.js';

export function getCurrentPageIdentifier() {
    return (
        window.location.pathname
            .replace(/^\/+|\/+$/g, '')
            .replace(/[\\/]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/-index$/i, '')
            .toLowerCase() || 'root'
    );
}

export function isSessionStorageAvailable() {
    try {
        const testKey = '__session_storage_test__';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        return true;
    } catch (error) {
        logWarning('Session storage is not available', { error: error.message });
        return false;
    }
}

export function saveWithPrefix(prefix, key, value) {
    try {
        const storageKey = `${prefix}${key}`;
        sessionStorage.setItem(storageKey, JSON.stringify(value));
        return true;
    } catch (error) {
        logError(error);
        return false;
    }
}

export function loadWithPrefix(prefix, key) {
    try {
        const storageKey = `${prefix}${key}`;
        const raw = sessionStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        logError(error);
        return null;
    }
}

export function removeWithPrefix(prefix, key) {
    try {
        const storageKey = `${prefix}${key}`;
        sessionStorage.removeItem(storageKey);
        return true;
    } catch (error) {
        logError(error);
        return false;
    }
}

export function clearByPrefix(prefix) {
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                sessionStorage.removeItem(key);
            }
        }
        return true;
    } catch (error) {
        logError(error);
        return false;
    }
}

export function isExpired(timestamp, maxAgeDays) {
    if (!timestamp) return true;
    const now = Date.now();
    const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
    return now - timestamp > maxAgeMs;
}

/**
 * Base Session Storage Manager
 * Provides common functionality for all session storage managers
 */
export class BaseSessionStorageManager {
    // Static registry to track all instances
    static instances = new Set();

    constructor(prefix, maxAgeDays = CONFIG.SESSION_STORAGE.MAX_AGE_DAYS) {
        this.prefix = prefix;
        this.maxAgeDays = maxAgeDays;

        // Register this instance
        BaseSessionStorageManager.instances.add(this);
    }

    /**
     * Check if session storage is available
     * @returns {boolean} True if available
     */
    isAvailable() {
        return isSessionStorageAvailable();
    }

    /**
     * Check if stored data is expired
     * @param {number} timestamp - Timestamp when data was stored
     * @returns {boolean} True if expired
     */
    isExpired(timestamp) {
        return isExpired(timestamp, this.maxAgeDays);
    }

    /**
     * Generate storage key for a specific page and optional sub-key
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} [subKey] - Optional sub-key (for grid-specific storage)
     * @returns {string} Storage key
     */
    getStorageKey(pageIdentifier, subKey = '') {
        const separator = subKey ? '__' : '';
        return `${this.prefix}${pageIdentifier}${separator}${subKey}`;
    }

    /**
     * Save data to session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {*} data - Data to save
     * @param {string} [subKey] - Optional sub-key
     * @returns {boolean} Success status
     */
    save(pageIdentifier, data, subKey = '') {
        try {
            if (!pageIdentifier) {
                logWarning('Invalid pageIdentifier for save');
                return false;
            }

            const storageKey = this.getStorageKey(pageIdentifier, subKey);
            const storageData = {
                values: data,
                timestamp: Date.now(),
                pageIdentifier: pageIdentifier,
                subKey: subKey
            };

            sessionStorage.setItem(storageKey, JSON.stringify(storageData));
            return true;
        } catch (error) {
            logError(error);
            return false;
        }
    }

    /**
     * Load data from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} [subKey] - Optional sub-key
     * @returns {*} Data or null if not found/expired
     */
    load(pageIdentifier, subKey = '') {
        try {
            if (!pageIdentifier) {
                logWarning('Invalid pageIdentifier for load');
                return null;
            }

            const storageKey = this.getStorageKey(pageIdentifier, subKey);
            const storedData = sessionStorage.getItem(storageKey);

            if (!storedData) {
                return null;
            }

            const data = JSON.parse(storedData);

            // Check if data is expired
            if (this.isExpired(data.timestamp)) {
                this.remove(pageIdentifier, subKey);
                return null;
            }

            return data.values;
        } catch (error) {
            logError(error);
            return null;
        }
    }

    /**
     * Remove data from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} [subKey] - Optional sub-key
     * @returns {boolean} Success status
     */
    remove(pageIdentifier, subKey = '') {
        try {
            if (!pageIdentifier) {
                return false;
            }

            const storageKey = this.getStorageKey(pageIdentifier, subKey);
            sessionStorage.removeItem(storageKey);
            return true;
        } catch (error) {
            logError(error);
            return false;
        }
    }

    /**
     * Clear all data for a specific page
     * @param {string} pageIdentifier - Unique identifier for the page
     * @returns {boolean} Success status
     */
    clearAllForPage(pageIdentifier) {
        return clearByPrefix(`${this.prefix}${pageIdentifier}__`);
    }

    /**
     * Clear all data with this prefix
     * @returns {boolean} Success status
     */
    clearAll() {
        return clearByPrefix(this.prefix);
    }

    /**
     * Static method to clear all data from all registered instances
     * @returns {boolean} Success status (true if all succeeded)
     */
    static clearAllInstances() {
        let allSucceeded = true;

        for (const instance of BaseSessionStorageManager.instances) {
            try {
                const result = instance.clearAll();
                if (!result) {
                    allSucceeded = false;
                }
            } catch (error) {
                logError(error);
                allSucceeded = false;
            }
        }

        return allSucceeded;
    }

    /**
     * Static method to get count of registered instances
     * @returns {number} Number of registered instances
     */
    static getInstanceCount() {
        return BaseSessionStorageManager.instances.size;
    }

    /**
     * Static method to get all registered prefixes
     * @returns {string[]} Array of all registered prefixes
     */
    static getAllPrefixes() {
        return Array.from(BaseSessionStorageManager.instances).map(instance => instance.prefix);
    }
}
